import type { WorkflowSummaryDto } from "@models/data/workflowSummaryDto";
import type { WorkflowDocument } from "./WorkflowDocument";

/** Builds the exact DynamoDB projection persisted for one workflow. */
export function buildWorkflowSummary(
  document: WorkflowDocument,
  ownerId: string,
): WorkflowSummaryDto {
  const workflowId = document.uid.trim();
  if (workflowId.length === 0) {
    throw new Error("Cannot persist a workflow summary without a workflow id.");
  }

  const trimmedOwnerId = ownerId.trim();
  if (trimmedOwnerId.length === 0) {
    throw new Error("Cannot persist a workflow summary without a project owner id.");
  }

  return {
    entity_type: "workflow",
    name: document.workflowName,
    owner_id: trimmedOwnerId,
    // Dynamo summaries only expose addressable steps. A step without an id is
    // still valid authored YAML, but cannot be referenced as a stable database
    // entity, so omit it and keep the persisted order contiguous.
    steps: document.steps
      .filter((step) => step.stepId.trim().length > 0)
      .map((step, index) => ({
        id: step.stepId.trim(),
        name: step.name,
        order: index + 1,
      })),
    workflow_id: workflowId,
  };
}

/** Stable comparison key for deciding whether the derived row changed. */
export function workflowSummarySignature(summary: WorkflowSummaryDto): string {
  return JSON.stringify(summary);
}
